import AsyncStorage from '@react-native-async-storage/async-storage';
import NetInfo from '@react-native-community/netinfo';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import type { Query } from '@tanstack/react-query';
import {
  defaultShouldDehydrateQuery,
  MutationCache,
  onlineManager,
  QueryClient,
} from '@tanstack/react-query';
import type { Persister } from '@tanstack/react-query-persist-client';
import { emitGlobalError } from './toastEmitter';

const CACHE_KEY = 'REACT_QUERY_OFFLINE_CACHE';
const MAX_AGE_MS = 1000 * 60 * 60 * 24; // 24 hours

// Public, anonymous content is excluded from the persisted cache. Storage
// exists so the authenticated app survives a cold start offline; a stored copy
// of the blog only means the previous visit's list paints before the network
// answers. These queries are ~1KB and revalidate via ETag, so there is nothing
// to gain and a stale first frame to lose.
//
// The access gate routes the landing CTAs. A restored "waitlist" answer keeps
// visitors in the queue after capacity returns, and React Query keeps it through
// a failed refetch — so with the backend down the landing would send people to
// the waitlist, where it must fall back to /login.
const UNPERSISTED_QUERY_PREFIXES = ['blog-feed', 'blog-post', 'access-gate'];

// The onboarding state decides a redirect in app/(app)/_layout.tsx, and a
// restored copy is a value from an earlier session. React Query reports
// isPending false the moment it has data, so the layout's loading guard is
// skipped and the gate decides from that stale value while the refetch is still
// in flight — which is how a finished org kept being sent back into the wizard
// after a reload. The backend is the only authority on whether onboarding is
// done, so this one query always waits for it. Other onboarding queries
// (profile, products) still persist; they inform the UI, not routing.
function isOnboardingStateQuery(queryKey: readonly unknown[]): boolean {
  return queryKey[0] === 'onboarding' && queryKey[1] === 'state';
}

function isPersistableQueryKey(queryKey: readonly unknown[]): boolean {
  return (
    !UNPERSISTED_QUERY_PREFIXES.includes(queryKey[0] as string) && !isOnboardingStateQuery(queryKey)
  );
}

export const dehydrateOptions = {
  shouldDehydrateQuery: (query: Query) =>
    defaultShouldDehydrateQuery(query) && isPersistableQueryKey(query.queryKey),
};

// The write filter only governs new writes, and restoring hydrates everything
// stored. A cache written before a query was excluded would otherwise bring it
// back on the next visit, so restore applies the same rule.
export function dropUnpersistedOnRestore(persister: Persister): Persister {
  return {
    ...persister,
    restoreClient: async () => {
      const stored = await persister.restoreClient();
      if (!stored) {
        return stored;
      }
      return {
        ...stored,
        clientState: {
          ...stored.clientState,
          queries: stored.clientState.queries.filter((query) =>
            isPersistableQueryKey(query.queryKey),
          ),
        },
      };
    },
  };
}

onlineManager.setEventListener((setOnline) => {
  return NetInfo.addEventListener((state) => {
    setOnline(!!state.isConnected);
  });
});

// refetchOnWindowFocus: false — prevents focus-refetch storms saturating the API on tab switch
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 5 * 60_000,
      gcTime: MAX_AGE_MS,
      refetchOnWindowFocus: false,
    },
    mutations: {
      // retry: 0 — mutations are writes; replaying a failed mutation can duplicate
      // non-idempotent operations (e.g. POST /notes, which has no unique guard) and
      // delays the MutationCache.onError toast behind a retry backoff. Safe automatic
      // retries require idempotency keys (tracked separately).
      retry: 0,
      // 'always' ensures network failures reject immediately (fire MutationCache.onError)
      // instead of being paused by the offline manager. This means the global toast
      // handler surfaces the error rather than silently queuing the mutation.
      // Trade-off: offline queueing is disabled for mutations app-wide. No flow in this
      // app relies on mutation queuing — all mutations require an active session.
      networkMode: 'always',
    },
  },
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (mutation?.meta?.skipGlobalErrorToast) return;
      emitGlobalError(error);
    },
  }),
});

export const asyncStoragePersister = dropUnpersistedOnRestore(
  createAsyncStoragePersister({
    storage: AsyncStorage,
    key: CACHE_KEY,
    throttleTime: 1000,
  }),
);

export async function clearQueryCache() {
  queryClient.clear();
  await AsyncStorage.removeItem(CACHE_KEY);
}
